import React from 'react';
import {useHistory} from 'react-router-dom';

import {type RouteConfigPath} from './route-config-path';
import {type PathGenerationArgs} from './path-generation-args';
import {generatePath} from './generate-path';

/**
 * @returns A typesafe function that can replace the current path with a new one.
 *
 * The function takes a variable number of arguments that are used to generate a path.
 * The arguments are the same as the ones used in `generateHash`.
 *
 * @example
 * ```tsx
 * const replaceWithPath = useReplaceWithPath();
 *
 * replaceWithPath('/account/childProfile/:childId/activity', {childId});
 * ```
 */
export const useReplaceWithPath = () => {
    const {replace} = useHistory();

    const replaceWithPath = React.useCallback(
        <T extends RouteConfigPath>(...args: PathGenerationArgs<T>) => {
            const hash = generatePath(args);
            replace(hash);
        },
        [replace],
    );

    return replaceWithPath;
};

export type ReplaceWithPath = ReturnType<typeof useReplaceWithPath>;
